import { z } from "zod";
import {
  COMPANY_SIZES,
  HOLISTIKA_SERVICE_TRACKS,
  PROJECT_PHASES,
  URGENCY_LEVELS,
  BUDGET_RANGES,
  INDUSTRIES,
  SPECIFIC_CHALLENGES,
  DECISION_MAKER_LEVELS,
  CONTACT_METHODS,
  SENIORITY_LEVELS,
  type HolistikaServiceTrack,
  type SessionMetadata
} from "../types/erp";

// ============================================
// VALIDATION SCHEMAS
// ============================================

// Helper for optional strings (trim empty to undefined)
const optionalString = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
  z.string().optional()
);

// Helper for optional URLs
const optionalUrl = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
  z.string().url("Please enter a valid URL (including https://)").optional()
);

// Helper for phone validation (international)
const phoneSchema = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
  z.string()
    .regex(/^[\+]?[1-9][\d\s\-\(\)\.]{7,15}$/, "Please enter a valid phone number")
    .optional()
);

// Main project intake form schema
export const ProjectIntakeFormSchema = z.object({
  // ============================================
  // CONTACT INFORMATION
  // ============================================
  firstName: z.string()
    .min(2, "Please enter your first name (at least 2 characters).")
    .max(50, "First name too long.")
    .transform(s => s.trim()),
    
  lastName: z.string()
    .min(2, "Please enter your last name (at least 2 characters).")
    .max(50, "Last name too long.")
    .transform(s => s.trim()),
    
  email: z.string()
    .email("Please enter a valid email address.")
    .max(255, "Email too long.")
    .transform(s => s.toLowerCase().trim()),
    
  companyName: z.string()
    .min(2, "Please enter your company name.")
    .max(200, "Company name too long.")
    .transform(s => s.trim()),
    
  jobTitle: optionalString,
  phone: phoneSchema,
  website: optionalUrl,
  
  // ============================================
  // COMPANY CONTEXT
  // ============================================
  companySize: z.enum(COMPANY_SIZES, {
    errorMap: () => ({ message: "Please select your company size." })
  }),
  
  industry: z.enum(INDUSTRIES, {
    errorMap: () => ({ message: "Please select your industry." })
  }),
  
  // Role context (for decision-making assessment)
  seniorityLevel: z.enum(SENIORITY_LEVELS).optional(),
  
  // ============================================
  // HOLISTIKA SERVICE MATCHING
  // ============================================
  serviceTrack: z.enum(HOLISTIKA_SERVICE_TRACKS, {
    errorMap: () => ({ message: "Please select which Holistika service track fits your needs." })
  }),
  
  projectPhase: z.enum(PROJECT_PHASES, {
    errorMap: () => ({ message: "Please indicate which project phase you're in." })
  }).optional(),
  
  urgency: z.enum(URGENCY_LEVELS, {
    errorMap: () => ({ message: "Please indicate your project timeline." })
  }),
  
  budgetRange: z.enum(BUDGET_RANGES, {
    errorMap: () => ({ message: "Please select your budget range." })
  }).optional(),
  
  // ============================================
  // BUSINESS INTELLIGENCE (Foresight Input)
  // ============================================
  currentSituation: z.string()
    .min(50, "Please provide more detail about your current situation (at least 50 characters).")
    .max(2000, "Please keep your situation description under 2000 characters.")
    .transform(s => s.trim()),
    
  specificChallenges: z.array(z.enum(SPECIFIC_CHALLENGES))
    .min(1, "Please select at least one challenge you're facing.")
    .max(5, "Please select no more than 5 main challenges."),
    
  desiredOutcome: z.string()
    .min(20, "Please describe what success looks like (at least 20 characters).")
    .max(1000, "Please keep desired outcome under 1000 characters.")
    .transform(s => s.trim())
    .optional(),
    
  successMetrics: optionalString,
  
  // ============================================
  // DECISION-MAKING CONTEXT (B2B Sales Critical)
  // ============================================
  decisionMaker: z.enum(DECISION_MAKER_LEVELS, {
    errorMap: () => ({ message: "Please indicate your role in decision-making." })
  }),
  
  stakeholdersInvolved: optionalString,
  
  previousConsultancy: z.boolean()
    .default(false),
    
  // Timeline for making decisions
  decisionTimeline: z.enum([
    'deciding_now',
    'within_month', 
    'next_quarter',
    'just_exploring'
  ]).optional(),
  
  // ============================================
  // COMMUNICATION PREFERENCES
  // ============================================
  preferredContactMethod: z.enum(CONTACT_METHODS)
    .default('email'),
    
  bestTimeToContact: z.enum([
    'morning_9_12',
    'afternoon_12_17', 
    'evening_17_20',
    'flexible'
  ]).optional(),
  
  timezone: z.string()
    .max(50)
    .optional(),
    
  // ============================================
  // STRATEGIC CONTEXT (Holistika Methodology)
  // ============================================
  // What they want to achieve strategically
  strategicGoals: z.array(z.enum([
    'enter_new_market',
    'scale_current_operations',
    'improve_efficiency',
    'digital_transformation',
    'team_optimization',
    'competitive_positioning',
    'cost_reduction',
    'revenue_growth',
    'risk_management',
    'innovation_adoption'
  ])).optional(),
  
  // Current blockers (Process Engineering focus)
  currentBlockers: z.array(z.enum([
    'lack_of_resources',
    'unclear_processes',
    'team_alignment_issues',
    'technology_limitations',
    'market_uncertainty',
    'funding_constraints',
    'regulatory_compliance',
    'competitive_pressure'
  ])).optional(),
  
  // ============================================
  // CONSENT & COMPLIANCE (GDPR)
  // ============================================
  consentContact: z.boolean()
    .refine((v) => v === true, {
      message: "Please confirm we can contact you about your project inquiry."
    }),
    
  consentMarketing: z.boolean()
    .default(false),
    
  consentDataProcessing: z.boolean()
    .refine((v) => v === true, {
      message: "Please consent to data processing as described in our privacy policy."
    }),
    
  // ============================================
  // METADATA
  // ============================================
  honeypot: z.string().max(0).optional(), // Anti-spam
  
});

// Session metadata schema (tracking & attribution)
export const SessionMetadataSchema = z.object({
  pageUrl: z.string().url().optional(),
  referrer: z.string().optional(), 
  userAgent: z.string().optional(),
  ipAddress: z.string().ip().optional(),
  ga4ClientId: z.string().optional(),
  
  // UTM parameters (marketing attribution)
  utm: z.object({
    source: z.string().optional(),
    medium: z.string().optional(),
    campaign: z.string().optional(),
    content: z.string().optional(),
    term: z.string().optional()
  }).optional(),
  
  // Additional tracking
  sessionId: z.string().optional(),
  timestamp: z.string().datetime().optional(),
  browserInfo: z.object({
    language: z.string().optional(),
    timezone: z.string().optional(),
    screenResolution: z.string().optional()
  }).optional()
}) satisfies z.ZodType<SessionMetadata>;

// Complete API request schema
export const ProjectIntakeApiSchema = z.object({
  formData: ProjectIntakeFormSchema,
  sessionMetadata: SessionMetadataSchema.optional()
});

// ============================================
// BUSINESS VALIDATION RULES
// ============================================

// Service track validation (business logic)
export const validateServiceTrackFit = (data: z.infer<typeof ProjectIntakeFormSchema>) => {
  const { serviceTrack, specificChallenges, companySize, budgetRange } = data;
  
  const warnings: string[] = [];
  
  // Think Big validation (startups/small companies)
  if (serviceTrack.startsWith('think_big') && companySize === 'enterprise_1000_plus') {
    warnings.push("Think Big is typically for smaller companies. Consider Lift & Ship for enterprise needs.");
  }
  
  // Budget vs service track alignment
  if (serviceTrack === 'envoy_tech_lab' && budgetRange === 'under_5k') {
    warnings.push("AI implementation projects typically require larger budgets. Consider consultation first.");
  }
  
  // Challenge vs track alignment
  if (specificChallenges.includes('ai_implementation_needed') && serviceTrack === 'think_big_startup') {
    warnings.push("AI implementation needs might be better served by Envoy Tech Lab.");
  }
  
  if (specificChallenges.includes('inefficient_processes') && serviceTrack === 'think_big_g2m') {
    warnings.push("Process efficiency challenges are typically handled by Lift & Ship.");
  }
  
  return warnings;
};

// Lead scoring preview (client-side)
export const calculateClientSideLeadScore = (data: z.infer<typeof ProjectIntakeFormSchema>): number => {
  let score = 0;
  
  // Budget scoring
  switch (data.budgetRange) {
    case '50k_plus': score += 40; break;
    case '15k_50k': score += 30; break; 
    case '5k_15k': score += 20; break;
    case 'under_5k': score += 10; break;
    default: score += 5;
  }
  
  // Urgency scoring
  switch (data.urgency) {
    case 'asap_2weeks': score += 25; break;
    case 'this_quarter': score += 20; break;
    case 'next_quarter': score += 10; break;
    default: score += 5;
  }
  
  // Service clarity scoring
  if (data.serviceTrack !== 'not_sure') score += 15;
  
  // Decision power scoring
  switch (data.decisionMaker) {
    case 'yes_iam': score += 25; break;
    case 'yes_with_approval': score += 18; break;
    case 'no_but_influencer': score += 10; break;
    default: score += 3;
  }
  
  // Company size scoring
  switch (data.companySize) {
    case 'enterprise_1000_plus': score += 15; break;
    case 'midsize_100_1000': score += 12; break; 
    case 'sme_10_100': score += 10; break;
    default: score += 8;
  }
  
  return Math.min(score, 100);
};

// ============================================
// DISPLAY HELPERS
// ============================================

export const getServiceTrackDisplay = (track: HolistikaServiceTrack): { label: string; description: string } => {
  const displays: Record<HolistikaServiceTrack, { label: string; description: string }> = {
    'think_big_startup': {
      label: 'Think Big - Startup Strategy',
      description: 'Complete strategic foundation for startups entering the market'
    },
    'think_big_g2m': {
      label: 'Think Big - Go-to-Market', 
      description: 'Specialized go-to-market strategy and execution'
    },
    'lift_ship_ops': {
      label: 'Lift & Ship - Operations',
      description: 'Transform and optimize your business operations'
    },
    'envoy_tech_lab': {
      label: 'Envoy Tech Lab - AI & Tech',
      description: 'AI implementation with ethical approach and business integration'
    },
    'mixed_engagement': {
      label: 'Mixed Engagement',
      description: 'Multiple service tracks needed (requires senior consultation)'
    },
    'not_sure': {
      label: 'Not Sure Yet',
      description: 'We\'ll help determine the best approach during discovery'
    }
  };
  
  return displays[track];
};

export const getChallengeDisplay = (challenge: string): string => {
  const displays: Record<string, string> = {
    'no_clear_strategy': 'No clear strategic direction',
    'poor_project_execution': 'Projects not delivering expected results',
    'inefficient_processes': 'Inefficient or unclear processes', 
    'digital_transformation_needs': 'Need digital transformation',
    'team_management_issues': 'Team alignment or management challenges',
    'data_governance_problems': 'Data management and governance issues',
    'market_positioning_unclear': 'Unclear market position or messaging',
    'scaling_challenges': 'Challenges scaling operations or team',
    'ai_implementation_needed': 'Need AI/automation implementation',
    'other': 'Other challenges (please specify in situation)'
  };
  
  return displays[challenge] || challenge;
};

export const getUrgencyDisplay = (urgency: string): { label: string; description: string } => {
  const displays: Record<string, { label: string; description: string }> = {
    'asap_2weeks': {
      label: 'ASAP (within 2 weeks)',
      description: 'Urgent need - we prioritize immediate response'
    },
    'this_quarter': {
      label: 'This quarter',
      description: 'Need to start within the next 3 months'
    }, 
    'next_quarter': {
      label: 'Next quarter',
      description: 'Planning ahead - start within 6 months'
    },
    'exploring': {
      label: 'Just exploring',
      description: 'Gathering information and exploring options'
    }
  };
  
  return displays[urgency] || { label: urgency, description: '' };
};

export const getBudgetDisplay = (budget: string): string => {
  const displays: Record<string, string> = {
    'under_5k': 'Under $5,000',
    '5k_15k': '$5,000 - $15,000',
    '15k_50k': '$15,000 - $50,000', 
    '50k_plus': '$50,000+',
    'not_disclosed': 'Prefer not to disclose'
  };
  
  return displays[budget] || budget;
};

export const getCompanySizeDisplay = (size: string): string => {
  const displays: Record<string, string> = {
    'startup_0_10': 'Startup (1-10 employees)',
    'sme_10_100': 'Small-Medium (10-100 employees)', 
    'midsize_100_1000': 'Mid-size (100-1,000 employees)',
    'enterprise_1000_plus': 'Enterprise (1,000+ employees)'
  };
  
  return displays[size] || size;
};

// ============================================
// CONDITIONAL VALIDATION (Business Logic)
// ============================================

// Validate service track vs company context
export const validateBusinessLogic = (data: z.infer<typeof ProjectIntakeFormSchema>) => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Enterprise companies with startup tracks
  if (data.companySize === 'enterprise_1000_plus' && data.serviceTrack.startsWith('think_big')) {
    warnings.push('Think Big services are typically designed for smaller companies. Consider Lift & Ship for enterprise transformation.');
  }
  
  // Very small budgets with complex services  
  if (data.budgetRange === 'under_5k' && data.serviceTrack === 'mixed_engagement') {
    warnings.push('Mixed engagement projects typically require larger budgets. We can start with a single track and expand later.');
  }
  
  // AI challenges without AI service track
  if (data.specificChallenges.includes('ai_implementation_needed') && 
      data.serviceTrack !== 'envoy_tech_lab' && 
      data.serviceTrack !== 'mixed_engagement') {
    warnings.push('Your challenges suggest Envoy Tech Lab might be a good fit. We can discuss this during discovery.');
  }
  
  // Process challenges without operations track
  if (data.specificChallenges.includes('inefficient_processes') &&
      data.serviceTrack !== 'lift_ship_ops' &&
      data.serviceTrack !== 'mixed_engagement') {
    warnings.push('Process optimization is typically handled by our Lift & Ship track.');
  }
  
  // Strategy challenges without Think Big
  if (data.specificChallenges.includes('no_clear_strategy') &&
      !data.serviceTrack.startsWith('think_big') &&
      data.serviceTrack !== 'mixed_engagement') {
    warnings.push('Strategic challenges are our Think Big specialty.');
  }
  
  // ASAP urgency without appropriate budget
  if (data.urgency === 'asap_2weeks' && 
      (data.budgetRange === 'under_5k' || data.budgetRange === 'not_disclosed')) {
    warnings.push('Urgent projects typically require immediate budget allocation. We can discuss options during discovery.');
  }
  
  return { errors, warnings };
};

// ============================================
// FORM STATE HELPERS
// ============================================

export const getDefaultFormValues = (): z.infer<typeof ProjectIntakeFormSchema> => ({
  firstName: '',
  lastName: '',
  email: '',
  companyName: '', 
  jobTitle: '',
  phone: '',
  website: '',
  companySize: 'sme_10_100', // Most common for Holistika
  industry: 'other',
  serviceTrack: 'not_sure',
  projectPhase: 'not_sure',
  urgency: 'this_quarter',
  budgetRange: 'not_disclosed',
  currentSituation: '',
  specificChallenges: [],
  desiredOutcome: '',
  successMetrics: '',
  decisionMaker: 'yes_with_approval', // Most common in B2B
  stakeholdersInvolved: '',
  previousConsultancy: false,
  decisionTimeline: 'within_month',
  preferredContactMethod: 'email',
  bestTimeToContact: 'flexible',
  timezone: '',
  strategicGoals: [],
  currentBlockers: [],
  consentContact: false,
  consentMarketing: false,
  consentDataProcessing: false,
  honeypot: ''
});

// Form progress calculation (for multi-step forms)
export const calculateFormProgress = (data: Partial<z.infer<typeof ProjectIntakeFormSchema>>): number => {
  const requiredFields = [
    'firstName', 'lastName', 'email', 'companyName', 
    'companySize', 'industry', 'serviceTrack', 'urgency',
    'currentSituation', 'specificChallenges', 'decisionMaker', 'consentContact'
  ];
  
  const completedFields = requiredFields.filter(field => {
    const value = data[field as keyof typeof data];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return true;
    return value && String(value).trim().length > 0;
  });
  
  return Math.round((completedFields.length / requiredFields.length) * 100);
};

// ============================================
// EXPORT TYPES
// ============================================

export type ProjectIntakeFormData = z.infer<typeof ProjectIntakeFormSchema>;
export type ProjectIntakeApiRequest = z.infer<typeof ProjectIntakeApiSchema>;

// Form field groups (for UI organization)
export const FORM_SECTIONS = {
  contact: ['firstName', 'lastName', 'email', 'companyName', 'jobTitle', 'phone', 'website'] as const,
  company: ['companySize', 'industry', 'seniorityLevel'] as const,
  service: ['serviceTrack', 'projectPhase', 'urgency', 'budgetRange'] as const,
  business: ['currentSituation', 'specificChallenges', 'desiredOutcome', 'successMetrics'] as const,
  decision: ['decisionMaker', 'stakeholdersInvolved', 'previousConsultancy', 'decisionTimeline'] as const,
  communication: ['preferredContactMethod', 'bestTimeToContact', 'timezone'] as const,
  strategy: ['strategicGoals', 'currentBlockers'] as const,
  consent: ['consentContact', 'consentMarketing', 'consentDataProcessing'] as const
} as const;
